'use client';

import React from 'react';
import { parseISO, format } from 'date-fns';
import { pluraliseWord } from '@fshr/shared';
import { useTranslations } from '@fshr/context/i18n-context';
import HorizontalLine from '../../horizontal-line';
import { handlePriceFormat } from './price-functions';
import BevSafeText from '../../bev-safe-text';
import { RoomRateType, StayResortFeesType } from '../../types/shopping-cart-item';

type ShoppingCartItemRateDetailsProps = {
	roomRate: RoomRateType;
	currencyCode: string;
	stayResortFees?: StayResortFeesType[];
	taxAndFees?: string;
};

export default function ShoppingCartItemRateDetails({
	roomRate,
	currencyCode,
	stayResortFees,
	taxAndFees,
}: ShoppingCartItemRateDetailsProps) {
	const t = useTranslations();

	return (
		<div className="w-full flex flex-col bg-surface spacing-card-padding-xs gap-[18px]">
			<div className="flex flex-row w-full justify-between">
				<div className="flex flex-col gap-[5px]">
					<span className="text-label">
						<span className="text-text-heading">{t('Average Nightly Rate')}</span>
					</span>

					<div className="text-caption">
						<span className="text-text-secondary">{t(taxAndFees ?? '')}</span>
					</div>
				</div>
				<span className="flex flex-col items-end justify-start text-label">
					<span className="text-text-primary italic">
						{handlePriceFormat({
							amount: roomRate?.averageNightlyPrice?.cash?.amount ?? 0,
							currencyCode,
						})}
					</span>
				</span>
			</div>
			<HorizontalLine additionalClasses="border-border-subtle" />
			<div className="flex flex-col gap-[10px] w-full">
				<div className="text-label">
					<span className="text-text-primary">
						{`${t('Daily Rates')} | ${pluraliseWord(
							roomRate?.nightlyRates?.length ?? 0,
							t('Night'),
							t('Nights')
						)}`}
					</span>
				</div>
				{roomRate?.nightlyRates?.map(({ date, cash, resortFees }) => {
					return (
						<React.Fragment key={date}>
							<div className="flex flex-row spacing-inline-s w-full justify-between">
								<span className="text-caption">
									<span className="text-text-primary">
										{date ? format(parseISO(date), 'EEEE, MMM dd') : ''}
									</span>
								</span>
								<span className="text-label">
									<span className="text-text-primary italic">
										{handlePriceFormat({
											amount: cash?.amount ?? 0,
											currencyCode,
										})}
									</span>
								</span>
							</div>
							{(resortFees?.length ?? 0) > 0
								? resortFees?.map(({ code, description, cash }) => {
										return (
											<div className="rate-details__pair" key={code}>
												<span className="ty-d4">{`+ ${description}`}</span>
												<span className="rate-details__pair__price">
													{handlePriceFormat({
														amount: cash?.amount ?? 0,
														currencyCode,
													})}
												</span>
											</div>
										);
									})
								: null}
						</React.Fragment>
					);
				})}
				{stayResortFees
					? stayResortFees.map(({ code, description, cash }) => {
							return (
								<div className="flex flex-row spacing-inline-s w-full justify-between" key={code}>
									<span className="text-caption">
										<span className="text-text-primary">
											<BevSafeText>{description}</BevSafeText>
										</span>
									</span>
									<span className="text-label">
										<span className="text-text-primary italic">
											{handlePriceFormat({
												amount: cash?.amount ?? 0,
												currencyCode,
											})}
										</span>
									</span>
								</div>
							);
						})
					: null}
			</div>
		</div>
	);
}
